<template>
  <article class="rating-card" aria-labelledby="user-info">
    <header v-if="userReview.user_gender === 'male'" class="user-info">
      <img :src="userReviewMaleIcon" class="gender-icon" alt="男性ユーザーのアイコン" height="39" width="39" />
      <span id="user-info">男性 | {{ userReview.user_age }}歳</span>
    </header>
    <header v-else class="user-info">
      <img :src="userReviewFemaleIcon" class="gender-icon" alt="女性ユーザーのアイコン" height="39" width="39" />
      <span id="user-info">女性 | {{ userReview.user_age }}歳</span>
    </header>

    <section class="rating" aria-label="Rating score">
      <span class="rating-score">{{ userReview.rating }}</span
      >点
      <rating-star class="rating-star" :number-star="userReview.rating" star-size="small" aria-hidden="true" />
      <span class="satisfaction-level">{{ userReviewRating.text }}</span>
      <img class="emotion-image" :src="userReviewRating.emotion_image" height="20" width="20" aria-hidden="true" />
    </section>

    <section class="good-points">
      <h4>良かった点</h4>
      <p>{{ userReview.satisfaction_reason_good }}</p>
    </section>

    <section class="bad-points">
      <h4>悪かった点</h4>
      <p>{{ userReview.satisfaction_reason_bad }}</p>
    </section>

    <footer>
      <time :datetime="userReview.answered_date">{{ userReview.answered_date }}</time>
    </footer>
  </article>
</template>

<script setup lang="ts">
import userReviewMaleIcon from '/assets/images/user_review_male.png'
import userReviewFemaleIcon from '/assets/images/user_review_female.png'
import emotionSmileBeam from '/assets/images/emotion_smile_beam.png'
import emotionSmile from '/assets/images/emotion_smile.png'
import emotionMeh from '/assets/images/emotion_meh.png'
import emotionFrown from '/assets/images/emotion_frown.png'
import emotionDizzy from '/assets/images/emotion_dizzy.png'

interface OrganizerUserReview {
  rating: number
  satisfaction_reason_good: string
  satisfaction_reason_bad: string
  user_gender: string
  user_age: number
  answered_date: string
}

interface RatingInfo {
  text: string
  emotion_image: string
}

type Rating = number

const RATING_LIST: Record<Rating, RatingInfo> = {
  5: { text: 'とても満足', emotion_image: emotionSmileBeam },
  4: { text: '満足', emotion_image: emotionSmile },
  3: { text: '普通', emotion_image: emotionMeh },
  2: { text: '不満', emotion_image: emotionFrown },
  1: { text: 'とても不満', emotion_image: emotionDizzy }
}

const { userReview } = defineProps<{ userReview: OrganizerUserReview }>()

const userReviewRating = computed<RatingInfo>(() => RATING_LIST[Math.round(userReview.rating)])
</script>

<style lang="scss" scoped>
.rating-card {
  --background-color: #{$old_lace};

  background-color: var(--background-color);
  padding-top: 2rem;
  padding-right: 1.6rem;
  padding-bottom: 2rem;
  padding-left: 1.6rem;

  border-radius: 3px;

  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-info {
  font-weight: 400;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding-bottom: 2px;
}

.rating {
  display: flex;
  align-items: center;
}

.rating-star {
  background-color: transparent;
  margin-right: revert;
  padding-right: 5px;
  padding-left: 5px;
}

.emotion-image {
  margin-left: 10px;
}

.rating-score,
.satisfaction-level {
  font-weight: bold;
}

.good-points,
.bad-points {
  h4 {
    font-weight: 700;
    font-size: 14px;
  }
}

footer {
  font-size: 12px;
  color: #555555;
}
</style>
